import { Routes, Route, useLocation } from "react-router-dom";
import Login from "./pages/login";
import Profile from "./pages/profile";
import Home from "./pages/home";
import LoggedInRoutes from "./routes/LoggedInRoutes";
import NotLoggedInRoutes from "./routes/NotLoggedInRoutes";
import { useSelector } from "react-redux";
import Activate from "./pages/home/activate";
import Reset from "./pages/reset";
import CreatePostPopup from "./components/createPostPopup";
import { useEffect, useReducer, useState, useCallback, useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import configureAxios from "./config/axiosConfig";
import { postsReducer } from "./functions/reducers";
import Friends from "./pages/friends";
import Chat from "./pages/chat";
import CreateGroup from "./components/chat/CreateGroup";
import Search from "./pages/search";
import SearchPage from "./pages/searchPage";
import Live from "./pages/live";
import ConferencePage from "./pages/conference";
import CallHistory from "./pages/callHistory";
import { initializeSocket, disconnectSocket } from "./socket";
import FriendshipNotification from "./components/friendshipNotification";
import { CallProvider, IncomingCall, ActiveCall } from "./components/call";
import { AIHub, ChatBot, AdvogadoAI, JornalistaAI } from "./pages/ai";
import Playlists from "./pages/playlists";
import { ThemeProvider } from "./contexts/ThemeContext";
// Editorial Feed Pages - Video & Social
import TikTokPage from "./pages/feeds/TikTokPage";
import YouTubePage from "./pages/feeds/YouTubePage";
import ReelsPage from "./pages/feeds/ReelsPage";
import ShortsPage from "./pages/feeds/ShortsPage";
import NetflixPage from "./pages/feeds/NetflixPage";
import GamingPage from "./pages/feeds/GamingPage";
import APKStorePage from "./pages/feeds/APKStorePage";
import AudioPage from "./pages/feeds/AudioPage";
import SpotifyPage from "./pages/feeds/SpotifyPage";
import LiveAudioPage from "./pages/feeds/LiveAudioPage";
import LiveStreamPage from "./pages/feeds/LiveStreamPage";
import MoviePortalPage from "./pages/feeds/MoviePortalPage";
import VideoBankPage from "./pages/feeds/VideoBankPage";
import ThreadsPage from "./pages/feeds/ThreadsPage";
import GifPage from "./pages/feeds/GifPage";
import StickerPage from "./pages/feeds/StickerPage";
// Editorial Feed Pages - News & Content
import CNNPage from "./pages/feeds/CNNPage";
import BlogPage from "./pages/feeds/BlogPage";
import PodcastPage from "./pages/feeds/PodcastPage";
import PortfolioPage from "./pages/feeds/PortfolioPage";
import LibraryPage from "./pages/feeds/LibraryPage";
import EbookPage from "./pages/feeds/EbookPage";
import PdfPage from "./pages/feeds/PdfPage";
// Editorial Feed Pages - Education
import CoursePage from "./pages/feeds/CoursePage";
import QuizPage from "./pages/feeds/QuizPage";
import TutorialPage from "./pages/feeds/TutorialPage";
import EstudentPage from "./pages/feeds/EstudentPage";
import SchoolPage from "./pages/feeds/SchoolPage";
import VotingPage from "./pages/feeds/VotingPage";
// Editorial Feed Pages - Gaming
import EsportsPage from "./pages/feeds/EsportsPage";
import GameReviewPage from "./pages/feeds/GameReviewPage";
// Editorial Feed Pages - Commerce
import EShopPage from "./pages/feeds/EShopPage";
import AliExpressPage from "./pages/feeds/AliExpressPage";
import RestaurantPage from "./pages/feeds/RestaurantPage";
import FoodDeliveryPage from "./pages/feeds/FoodDeliveryPage";
import BookingPage from "./pages/feeds/BookingPage";
import AirbnbPage from "./pages/feeds/AirbnbPage";
import RentalPage from "./pages/feeds/RentalPage";
import UberPage from "./pages/feeds/UberPage";
import JobServicePage from "./pages/feeds/JobServicePage";
import EventPage from "./pages/feeds/EventPage";
import ClassifiedPage from "./pages/feeds/ClassifiedPage";
// Editorial Feed Pages - Health & Social
import MedicalPage from "./pages/feeds/MedicalPage";
import OngPage from "./pages/feeds/OngPage";
import CrowdfundingPage from "./pages/feeds/CrowdfundingPage";
import CommunityPage from "./pages/feeds/CommunityPage";
import PetitionPage from "./pages/feeds/PetitionPage";
// Editorial Feed Pages - Wellness & Lifestyle
import TravelPage from "./pages/feeds/TravelPage";
import CarPage from "./pages/feeds/CarPage";
import YogaPage from "./pages/feeds/YogaPage";
import ExercisePage from "./pages/feeds/ExercisePage";
import RecipePage from "./pages/feeds/RecipePage";
import FashionPage from "./pages/feeds/FashionPage";
import BeautyPage from "./pages/feeds/BeautyPage";
import PortfolioDetailPage from "./pages/feeds/PortfolioDetailPage";
import FeedsHubPage from "./pages/feeds/FeedsHubPage";
import SettingsPage from "./pages/settings";
import BlogCreatePage from "./pages/blog/BlogCreatePage";

function App() {
  const [visible, setVisible] = useState(false);
  const [initialEditorialType, setInitialEditorialType] = useState(null);
  const { user, darkTheme } = useSelector((state) => ({ ...state }));
  const [friendshipNotifications, setFriendshipNotifications] = useState([]);
  const socketRef = useRef(null);
  const location = useLocation();
  const [{ loading, posts }, dispatch] = useReducer(postsReducer, {
    loading: false,
    posts: [],
    error: "",
  });

  // Check if we're on the chat page (which has its own call handling)
  const isOnChatPage = location.pathname.startsWith("/chat");

  // Configure axios on app initialization
  useEffect(() => {
    configureAxios();
  }, []);

  // Monitor location state to handle createPost navigation from feeds
  useEffect(() => {
    if (location.state?.createPost) {
      console.log("📝 Opening CreatePostPopup with type:", location.state.createPost);
      setVisible(true);
      setInitialEditorialType(location.state.createPost);
    }
  }, [location.state?.createPost]);

  // Initialize socket connection when user is logged in
  useEffect(() => {
    if (user?.token) {
      const socket = initializeSocket(user.token);
      socketRef.current = socket;

      // Listen for friendship events
      socket.on("friendship:request_received", (data) => {
        setFriendshipNotifications((prev) => [...prev, { ...data, id: Date.now() }]);
      });

      socket.on("friendship:request_accepted", (data) => {
        setFriendshipNotifications((prev) => [...prev, { ...data, id: Date.now() }]);
      });

      socket.on("friendship:unfriended", (data) => {
        // Can trigger UI updates if needed
        console.log("Unfriended by:", data.userId);
      });

      socket.on("friendship:request_cancelled", (data) => {
        // Can trigger UI updates if needed
        console.log("Request cancelled by:", data.userId);
      });

      socket.on("friendship:request_deleted", (data) => {
        // Can trigger UI updates if needed
        console.log("Request deleted by:", data.userId);
      });

      return () => {
        socket.off("friendship:request_received");
        socket.off("friendship:request_accepted");
        socket.off("friendship:unfriended");
        socket.off("friendship:request_cancelled");
        socket.off("friendship:request_deleted");
        disconnectSocket();
      };
    }
  }, [user?.token]);

  const removeNotification = useCallback((id) => {
    setFriendshipNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user?.token) {
      getAllPosts();
    }
  }, [user?.token]);
  const getAllPosts = useCallback(async () => {
    try {
      dispatch({
        type: "POSTS_REQUEST",
      });
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/getAllposts`,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );
      const { data } = response || {};
      dispatch({
        type: "POSTS_SUCCESS",
        payload: data,
      });
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || "Erro ao carregar posts";
      dispatch({
        type: "POSTS_ERROR",
        payload: errorMessage,
      });
    }
  }, [dispatch, user?.token]);

  // Wrapper function to handle closing the popup and clearing editorial type
  const handleSetVisible = useCallback((value) => {
    setVisible(value);
    if (!value) {
      setInitialEditorialType(null);
    }
  }, []);

  return (
    <ThemeProvider>
      <div className={darkTheme ? "dark" : undefined}>
        <CallProvider socket={socketRef.current} disabled={isOnChatPage}>
          {visible && (
            <CreatePostPopup
              user={user}
              setVisible={handleSetVisible}
              posts={posts}
              dispatch={dispatch}
              initialEditorialType={initialEditorialType}
            />
          )}
          {/* Call Components - Only show when NOT on chat page (chat has its own call UI) */}
          {!isOnChatPage && (
            <>
              <IncomingCall />
              <ActiveCall />
            </>
          )}
          {/* Friendship Notifications */}
          <div className="friendship_notifications_container">
            {friendshipNotifications.map((notification) => (
              <FriendshipNotification
                key={notification.id}
                notification={notification}
                onClose={() => removeNotification(notification.id)}
              />
            ))}
          </div>
          <Routes>
            <Route element={<LoggedInRoutes />}>
              <Route
                path="/profile"
                element={
                  <Profile setVisible={handleSetVisible} getAllPosts={getAllPosts} />
                }
                exact
              />
              <Route
                path="/profile/:username"
                element={
                  <Profile setVisible={handleSetVisible} getAllPosts={getAllPosts} />
                }
                exact
              />
              <Route
                path="/friends"
                element={
                  <Friends setVisible={handleSetVisible} getAllPosts={getAllPosts} />
                }
                exact
              />
              <Route
                path="/friends/:type"
                element={
                  <Friends setVisible={handleSetVisible} getAllPosts={getAllPosts} />
                }
                exact
              />
              <Route
                path="/"
                element={
                  <Home
                    setVisible={handleSetVisible}
                    posts={posts}
                    loading={loading}
                    getAllPosts={getAllPosts}
                    dispatch={dispatch}
                  />
                }
                exact
              />
              <Route path="/activate/:token" element={<Activate />} exact />
              <Route path="/chat" element={<Chat />} exact />
              <Route path="/chat/new-group" element={<CreateGroup />} exact />
              <Route path="/chat/:odIus" element={<Chat />} exact />
              <Route
                path="/search"
                element={<Search setVisible={handleSetVisible} />}
                exact
              />
              <Route
                path="/search-advanced"
                element={<SearchPage setVisible={handleSetVisible} />}
                exact
              />
              <Route
                path="/live"
                element={<Live socket={socketRef.current} />}
                exact
              />
              <Route
                path="/conference"
                element={<ConferencePage socket={socketRef.current} />}
                exact
              />
              <Route
                path="/conference/:code"
                element={<ConferencePage socket={socketRef.current} />}
                exact
              />
              <Route path="/calls" element={<CallHistory />} exact />
              <Route path="/ai" element={<AIHub />} exact />
              <Route path="/ai/chatbot" element={<ChatBot />} exact />
              <Route path="/ai/advogado" element={<AdvogadoAI />} exact />
              <Route path="/ai/jornalista" element={<JornalistaAI />} exact />
              <Route path="/playlists" element={<Playlists />} exact />
              <Route path="/playlists/watch/:playlistId" element={<Playlists />} exact />
              <Route path="/playlists/:type" element={<Playlists />} exact />
              <Route path="/feeds" element={<FeedsHubPage />} exact />
              {/* Editorial Feed Routes - Video & Social */}
              <Route path="/short-video" element={<TikTokPage />} exact />
              <Route path="/long-video" element={<YouTubePage />} exact />
              <Route path="/vertical-clip" element={<ReelsPage />} exact />
              <Route path="/quick-video" element={<ShortsPage />} exact />
              <Route path="/streaming" element={<NetflixPage />} exact />
              <Route path="/gaming" element={<GamingPage />} exact />
              <Route path="/apk-store" element={<APKStorePage />} exact />
              <Route path="/audio" element={<AudioPage />} exact />
              <Route path="/music" element={<SpotifyPage />} exact />
              <Route path="/live-audio" element={<LiveAudioPage />} exact />
              <Route path="/live-stream" element={<LiveStreamPage />} exact />
              <Route path="/movie-portal" element={<MoviePortalPage />} exact />
              <Route path="/video-bank" element={<VideoBankPage />} exact />
              <Route path="/threads" element={<ThreadsPage />} exact />
              <Route path="/gif" element={<GifPage />} exact />
              <Route path="/sticker" element={<StickerPage />} exact />
              {/* Editorial Feed Routes - News & Content */}
              <Route path="/news" element={<CNNPage />} exact />
              <Route path="/blog" element={<BlogPage />} exact />
              <Route path="/blog/create" element={<BlogCreatePage />} exact />
              <Route path="/blog/edit/:postId" element={<BlogCreatePage />} exact />
              <Route path="/podcast" element={<PodcastPage />} exact />
              <Route path="/portfolio" element={<PortfolioPage />} exact />
              <Route path="/portfolio/:postId" element={<PortfolioDetailPage />} exact />
              <Route path="/library" element={<LibraryPage />} exact />
              <Route path="/ebook" element={<EbookPage />} exact />
              <Route path="/pdf" element={<PdfPage />} exact />
              {/* Editorial Feed Routes - Education */}
              <Route path="/course" element={<CoursePage />} exact />
              <Route path="/quiz" element={<QuizPage />} exact />
              <Route path="/tutorial" element={<TutorialPage />} exact />
              <Route path="/estudent" element={<EstudentPage />} exact />
              <Route path="/school" element={<SchoolPage />} exact />
              <Route path="/voting" element={<VotingPage />} exact />
              {/* Editorial Feed Routes - Gaming */}
              <Route path="/esports" element={<EsportsPage />} exact />
              <Route path="/game-review" element={<GameReviewPage />} exact />
              {/* Editorial Feed Routes - Commerce */}
              <Route path="/eshop" element={<EShopPage />} exact />
              <Route path="/marketplace" element={<AliExpressPage />} exact />
              <Route path="/restaurant" element={<RestaurantPage />} exact />
              <Route path="/food-delivery" element={<FoodDeliveryPage />} exact />
              <Route path="/booking" element={<BookingPage />} exact />
              <Route path="/lodging" element={<AirbnbPage />} exact />
              <Route path="/rental" element={<RentalPage />} exact />
              <Route path="/mobility" element={<UberPage />} exact />
              <Route path="/job-service" element={<JobServicePage />} exact />
              <Route path="/event" element={<EventPage />} exact />
              <Route path="/classified" element={<ClassifiedPage />} exact />
              {/* Editorial Feed Routes - Health & Social */}
              <Route path="/medical" element={<MedicalPage />} exact />
              <Route path="/ong" element={<OngPage />} exact />
              <Route path="/crowdfunding" element={<CrowdfundingPage />} exact />
              <Route path="/community" element={<CommunityPage />} exact />
              <Route path="/petition" element={<PetitionPage />} exact />
              {/* Editorial Feed Routes - Wellness & Lifestyle */}
              <Route path="/travel" element={<TravelPage />} exact />
              <Route path="/car" element={<CarPage />} exact />
              <Route path="/yoga" element={<YogaPage />} exact />
              <Route path="/exercise" element={<ExercisePage />} exact />
              <Route path="/recipe" element={<RecipePage />} exact />
              <Route path="/fashion" element={<FashionPage />} exact />
              <Route path="/beauty" element={<BeautyPage />} exact />
              <Route path="/settings" element={<SettingsPage />} exact />
            </Route>
            <Route element={<NotLoggedInRoutes />}>
              <Route path="/login" element={<Login />} exact />
            </Route>
            <Route path="/reset" element={<Reset />} />
          </Routes>
        </CallProvider>
      </div>
    </ThemeProvider>
  );
}

export default App;
